import { Dispatch, useEffect, useState } from "react"
import { CampProps, CampingStyleProps, SiteProps } from "../interfaces"
import { useCookies } from "react-cookie"
import api from "../api2"
import * as Sentry from "@sentry/react"
import ICampLite from "~/interfaces/ICampLite"
import useSites from "~/api2/hooks/useSites"
import { useCamp } from "~/api2/hooks/useCamp"
import { useCampContext } from "~/contexts/CampProvider"

const handleError = (err: any, camp: ICampLite, resource: string) => {
  console.error(err)
  if (err["status"]) {
    Sentry.captureMessage(`Error fetching camp camping styles with status code ${err.status}`, {
      level: "error",
      extra: {
        status: err.status,
        message: err.message,
        campId: camp.id,
        campName: camp.name,
      }
    })
  } else {
    Sentry.captureException(err)
  }
}

const cookieCurrentCampKey = 'park-admin-current-camp-id'

const useAdminCamp = (
  availableCamps: ICampLite[],
  fallbackCampId: string
) => {
  const [cookies, setCookie] = useCookies([cookieCurrentCampKey])
  const [campingStyles, setCampingStyles] = useState<CampingStyleProps[]>()
  const { campId, setCampId } = useCampContext()
  const {
    index: { data: sites },
  } = useSites(campId)
  const {
    index: { data: camp, isLoading: isLoadingCamp },
  } = useCamp(campId)

  useEffect(() => {
    if (!availableCamps || availableCamps?.length === 0) return

    const defaultCampId = availableCamps.length > 1 ? availableCamps[0].id : fallbackCampId

    if (cookies[cookieCurrentCampKey] && defaultCampId !== cookies[cookieCurrentCampKey]) {
      const defaultCamp = availableCamps.find((c) => c?.id === defaultCampId)
      const selectedCamp = availableCamps.find((c) => c?.id === cookies[cookieCurrentCampKey])
      setCampId(selectedCamp?.id || defaultCamp?.id)
    } else {
      setCampId(availableCamps[0].id)
    }
  }, [availableCamps])

  useEffect(() => {
    if (!campId || !camp) return

    setCookie(cookieCurrentCampKey, campId)
    if (camp) {
      Sentry.setTag('campName', camp.name)
      Sentry.setExtra("campName", camp.name)
    }

    api.Me.camping_styles_for_camp(campId)
      .then((camping_styles) => {
        setCampingStyles(camping_styles)
      })
      .catch((err) => {
        if (camp) {
          handleError(err, camp, "camping styles")
        }
      })

  }, [campId, camp])

  const setCamp = (newCamp: ICampLite) => {
    setCampId(newCamp.id)
  }

  return { camp, campingStyles, sites, setCamp, setCampingStyles, isLoadingCamp }
}

export default useAdminCamp
